import type { SlotMapping, SourceFieldKind } from '@ludus/core'

export type MappableSlot = 'stageSource' | 'groupKey' | 'tags' | 'numericValue'

export interface SlotSpec {
  slot: MappableSlot
  /** A field whose kind is outside this list cannot feed the slot, so it is not offered. */
  accepts: readonly SourceFieldKind[]
}

/**
 * `SlotMapping` carries nine keys; these four are the ones an operator picks a
 * field for. The rest (`displayName`, `assigneeExternalRef`, `closeState`, the
 * two timestamps) are derived by the adapter from the item itself — GitHub reads
 * the owner from `issue.assignees`, never from a mapped field — so offering them
 * would be a control whose choice changes nothing.
 *
 * None is required. `stageSource` in particular is unset by a source whose stage
 * axis is fixed rather than carried by a pickable field, and GitHub falls back to
 * the conventional `Status` column when it is absent — so demanding it here would
 * lock out the next adapter and over-ask this one.
 *
 * Which slots a given source can fill is not something discovery reports yet; the
 * stand-in is to offer only slots some field on the chosen board could feed.
 * Tracked separately — see the PR for #64.
 */
export const SLOT_SPECS: readonly SlotSpec[] = [
  { slot: 'stageSource', accepts: ['single_select'] },
  { slot: 'groupKey', accepts: ['single_select'] },
  { slot: 'tags', accepts: ['multi_select'] },
  { slot: 'numericValue', accepts: ['number'] },
]

export const OFF = ''

export function toSlotMapping(picks: Record<MappableSlot, string>): SlotMapping {
  const mapping: SlotMapping = {}
  for (const { slot } of SLOT_SPECS) {
    const picked = picks[slot]
    if (picked !== OFF) mapping[slot] = picked
  }
  return mapping
}
